import { asArray, round2, sumOf, legacySum } from './promoPrimitives';
import { getLocalOfferInfos, negoRowsOf, toRef } from './promoModel';
import { offerTotalUB } from './promoUnits';

// Values and totals of a negotiation (#23521). A line value is volume UC × price (decision "mécanisme" of the spec),
// a free product never counts in the value but its volume does (RG-11), and the statement never accumulates : the
// campaign gauges and offer totals keep showing the negotiated references (RG-09).

/** Account price of a product for a campaign (`salesHelper.assort.getProductAccountValue` with `promoValueToUse`). */
export const priceOf = (campaign, productId) => Number(campaign?.prices?.[productId]) || 0;

const sameProduct = (row, ref) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === ref.productId;

/**
 * `sfaHelper.campaign.calculProductValue` for every paying row of one product. The main row takes the current account
 * price ; an additional presence has no price of its own (RG-09) but is valued at the main row's price, so that the
 * offer total, the campaign gauge and the header KPI add up to the row value the user sees (spec figure 3 : the negotiated
 * value of a product covers all its presences). `_totalValue` is the product total, kept on the main row. Free rows keep
 * their stored value, as the legacy free handlers do. RG-03 : a volume back to 0 gives a 0 value (the legacy fell back to
 * another row's volume in that case).
 */
export function refreshProductValues(campaign, rows, ref, productId) {
	const scope = { ...ref, productId };
	const price = priceOf(campaign, productId);
	const additionalUC = sumOf(
		asArray(rows).filter((row) => sameProduct(row, scope) && row.additional),
		'volumeUC'
	);
	return asArray(rows).map((row) => {
		if (!sameProduct(row, scope) || row.isFree) return row;
		const next = { ...row };
		const volumeUC = Number(next.volumeUC) || 0;
		if (next.additional) {
			next.value = round2(price * volumeUC);
			return next;
		}
		next.statedValue = price;
		next.value = round2(price * volumeUC);
		next._totalValue = round2(price * (volumeUC + additionalUC));
		return next;
	});
}

const productKey = (row) => `${row.campaignId}|${row.campaignOfferId}|${row.productId}`;

/**
 * The rule of `refreshProductValues` applied to a whole list of negotiated rows, with the price the legacy reads for each
 * campaign and product (`priceOf(campaignId, productId)`, account price with the campaign's `promoValueToUse`). Used by
 * the legacy for the rows it loads without an edit (the previous visit's negotiations, the local offers) : they were
 * valued by `sfaHelper.campaign.calculProductValue`, which left the additional presences at no value. Free rows are kept.
 */
export function refreshNegoValues(rows, priceOf) {
	const list = asArray(rows);
	const additionalUC = new Map();
	list.forEach((row) => {
		if (!row?.additional || row.isFree) return;
		additionalUC.set(productKey(row), (additionalUC.get(productKey(row)) || 0) + (Number(row.volumeUC) || 0));
	});
	return list.map((row) => {
		if (!row || row.isFree) return row;
		const price = Number(typeof priceOf === 'function' ? priceOf(row.campaignId, row.productId) : 0) || 0;
		const volumeUC = Number(row.volumeUC) || 0;
		if (row.additional) return { ...row, value: round2(price * volumeUC) };
		return { ...row, statedValue: price, value: round2(price * volumeUC), _totalValue: round2(price * (volumeUC + (additionalUC.get(productKey(row)) || 0))) };
	});
}

/**
 * Negotiated value of a product : the stored value of its paying presences (main line + additional ones), as the offer
 * total adds them up. `_totalValue`, kept on the main row while editing, is not stored by the API : a consulted visit only
 * has the row values.
 */
export const productNegoValue = (rows, ref, productId) =>
	round2(
		sumOf(
			asArray(rows).filter((row) => sameProduct(row, { ...ref, productId }) && !row.isFree),
			'value'
		)
	);

/** Negotiated totals of one offer : volume UC (paying, free and additional rows), value (free rows excluded), UB total or mixed units. */
export function offerTotals(rows, ref) {
	const scope = asArray(rows).filter((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId);
	return {
		volumeUC: sumOf(scope, 'volumeUC'),
		value: round2(
			sumOf(
				scope.filter((row) => !row.isFree),
				'value'
			)
		),
		ub: offerTotalUB(scope, ref),
	};
}

/** All the negotiated rows a campaign's gauges add up : the national arrays, or every local offer of the campaign (`updateObjectivesTotals`). */
export function campaignNegoRows(snapshot, campaign) {
	if (campaign?.promoLocal) return getLocalOfferInfos(snapshot, campaign.id).flatMap((oi) => asArray(oi._negociatedCampaignOffers));
	return asArray(snapshot?.model?.negotiated).filter((row) => row.campaignId === campaign?.id);
}

/**
 * Campaign gauges (`updateObjectivesTotals`, JS:2468-2548) : negotiated volume and value, with the objectives computed by
 * the legacy from the precos. Without objective the gauge is full.
 */
export function campaignGauges(snapshot, campaign) {
	const rows = campaignNegoRows(snapshot, campaign);
	const volumeUC = sumOf(rows, 'volumeUC');
	const value = round2(
		sumOf(
			rows.filter((row) => !row.isFree),
			'value'
		)
	);
	const objectives = campaign?.objectives?.campaign || {};
	const gauge = (total, objective) => ({ total, objective: objective || 0, percent: objective ? round2((total * 100) / objective) : 100 });
	return { volume: gauge(volumeUC, objectives.objectiveVOL), value: gauge(value, objectives.objectiveCA) };
}

/** Objective of one offer, for the offer header ("74 UC / 120 UC"). */
export const offerObjectives = (campaign, offerId) => campaign?.objectives?.offers?.[offerId] || { objectiveVOL: 0, objectiveCA: 0 };

/** Per-row preconisation of a product (D5), shown under the negotiated cell when it exists. */
export function productPreco(rows, ref, productId) {
	const items = asArray(rows).filter((row) => row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId && row.productId === productId && !row.additional);
	return { volumeUC: legacySum(items.map((row) => Number(row.precoVolumeUC) || 0)), value: legacySum(items.map((row) => Number(row.precoValue) || 0)) };
}

/** Totals of an offer from the snapshot. */
export const offerTotalsOf = (snapshot, campaignId, campaignOfferId, offerKey = null) => {
	const ref = toRef(campaignId, campaignOfferId, offerKey);
	return offerTotals(negoRowsOf(snapshot, ref), ref);
};
